import styled from 'astroturf/react';
import { Body } from 'js/design_system/Body';
import { Heading } from 'js/design_system/Heading';
import { Icon } from 'js/design_system/Icon';
import SourcePaperCard from './SourcePaperCard';
import { TextButton } from 'js/design_system/TextButton';
import { useState } from 'react';
import { RelatedClaim, Work, WorkClaim } from './types';
import {
  CONTRADICTING_CLAIMS_ELEMENT_ID,
  SUPPORTING_CLAIMS_ELEMENT_ID,
} from './SingleClaimPage';
import Badge from 'js/design_system/Badge';
import trackClick from 'js/utils/trackClick';

type ClaimDetailsProps = {
  claim: WorkClaim;
  quotedEvidence: string;
  openCitationsModal: () => void;
  sourcePaper: Work;
  researchInterests: string[];
  supportingClaims: RelatedClaim[];
  contradictingClaims: RelatedClaim[];
};

const Card = styled.div`
  background: var(--background-white, #fff);
  border: 1px solid var(--stroke-light, #dddde2);
  border-radius: var(--corner-radius-radius-lg, 16px);
  overflow: hidden;
`;

const CardDividerSection = styled.div`
  border-top: 1px solid var(--stroke-light, #dddde2);
  padding: var(--spacing-xs-16, 16px) var(--spacing-sm-32, 32px);

  @media (max-width: 341px) {
    padding: var(--spacing-xs-16, 16px) var(--spacing-sm-24, 24px);
  }
`;

const CardSectionHeader = styled.div`
  display: flex;
  gap: var(--spacing-xs-8, 8px);
  align-items: flex-start;
  margin-bottom: 12px;
`;

const ClaimCardTop = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs-16, 16px);
  padding: var(--spacing-sm-32, 32px) var(--spacing-sm-32, 32px) 0;

  @media (max-width: 341px) {
    padding: var(--spacing-sm-32, 32px) var(--spacing-sm-24, 24px) 0;
  }
`;

const ClaimCardSourcePaper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs-8, 8px);
  margin: var(--spacing-sm-24, 24px) 0;
  padding: 0 var(--spacing-sm-32, 32px);

  @media (max-width: 341px) {
    padding: 0 var(--spacing-sm-24, 24px);
  }
`;

const ClaimTitle = styled(Heading)`
  font-size: var(--type-sizes-32, 32px);
  line-height: 38px;

  @media (max-width: 341px) {
    font-size: var(--type-sizes-24, 24px);
    line-height: 28px;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs-16, 16px);

  &:last-child {
    margin-bottom: 0;
  }
`;

const EvidenceQuoteWrap = styled.div`
  /* Required for max-height calc, fade height, and collapsed quote line-height. */
  --quoted-evidence-line-height: 20px;
  position: relative;

  &[data-expanded='false'] {
    max-height: calc(var(--quoted-evidence-line-height) * 3);
    overflow: hidden;
  }
`;

const EvidenceQuote = styled.p`
  margin: 0;
  padding: 0;
  background: var(--accent-green, #ccffd4);
  font-style: italic;
  color: var(--text-primary, #141413);
  line-height: var(--type-line-heights-24, 24px);

  &[data-expanded='false'] {
    line-height: var(--quoted-evidence-line-height);
  }
`;

const ReadMoreButton = styled(TextButton)`
  color: var(--text-primary, #141413);
  font-weight: var(--type-font-weight-regular);

  &[type='button'] {
    height: auto;
    min-height: 0;
    min-width: 0;
    padding: 0;
    line-height: var(--quoted-evidence-line-height);
  }
`;

const ReadMoreButtonText = styled.span`
  text-decoration: underline;
`;

const ReadMoreFade = styled.div`
  display: flex;
  position: absolute;
  right: 0;
  bottom: 0;
  align-items: center;
  box-sizing: border-box;
  height: var(--quoted-evidence-line-height);
  padding-left: var(--spacing-md-48, 48px);
  background: linear-gradient(
    90deg,
    rgba(204, 255, 212, 0),
    var(--accent-green, #ccffd4) 30%
  );
`;

const ScrollToClaimsButton = styled(TextButton)`
  border-top: 1px solid var(--stroke-light, #dddde2);
  color: var(--text-primary, #141413);
  justify-content: left;
  padding: 0 var(--spacing-sm-32, 32px) !important;
  width: 100%;

  @media (max-width: 341px) {
    padding: 0 var(--spacing-sm-24, 24px) !important;
  }
`;

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-xs-8, 8px);
`;

const ClaimDetails: React.FunctionComponent<ClaimDetailsProps> = ({
  claim,
  quotedEvidence,
  openCitationsModal,
  sourcePaper,
  researchInterests,
  supportingClaims,
  contradictingClaims,
}) => {
  const [quoteOpen, setQuoteOpen] = useState(false);

  return (
    <Container>
      <Body bold size="lg" as="h2">
        Claim details
      </Body>
      <Card>
        <ClaimCardTop>
          <Body bold size="md">
            Research claim
          </Body>
          <ClaimTitle as="h1">{claim.claim}</ClaimTitle>
          {researchInterests.length > 0 ? (
            <TagRow>
              {researchInterests.map((researchInterest) => (
                <Badge
                  key={researchInterest}
                  text={researchInterest}
                  color="#3A372F"
                  backgroundColor="var(--background-beige, #f9f7f4)"
                  style={{ border: '1px solid #E2E0D9' }}
                />
              ))}
            </TagRow>
          ) : null}
        </ClaimCardTop>

        {sourcePaper ? (
          <ClaimCardSourcePaper>
            <SourcePaperCard
              sourceClaim={claim}
              paper={sourcePaper}
              showHeader={true}
              openCitationsModal={openCitationsModal}
              thumbnailWidth={75}
              thumbnailHeight={110}
            />
          </ClaimCardSourcePaper>
        ) : null}

        {quotedEvidence ? (
          <CardDividerSection>
            <CardSectionHeader>
              <Icon name="format_ink_highlighter" size={20} />
              <Body bold size="md" as="span">
                Quoted evidence
              </Body>
            </CardSectionHeader>
            <EvidenceQuoteWrap data-expanded={quoteOpen ? 'true' : 'false'}>
              <EvidenceQuote
                data-expanded={quoteOpen ? 'true' : 'false'}
              >{`"${quotedEvidence}"`}</EvidenceQuote>
              {!quoteOpen ? (
                <ReadMoreFade>
                  <ReadMoreButton
                    type="button"
                    inline
                    onClick={() => {
                      trackClick({
                        source: 'single_claim_page.quoted_evidence.read_more',
                        extra: {
                          source_claim_id: claim.id,
                        },
                      });
                      setQuoteOpen(true);
                    }}
                  >
                    <span>...</span>
                    <ReadMoreButtonText>Read more</ReadMoreButtonText>
                  </ReadMoreButton>
                </ReadMoreFade>
              ) : null}
            </EvidenceQuoteWrap>
            {quoteOpen ? <div style={{ marginTop: 8 }}></div> : null}
          </CardDividerSection>
        ) : null}

        {supportingClaims.length > 0 && (
          <ScrollToClaimsButton
            icon="stack"
            onClick={() => {
              trackClick({
                source: 'single_claim_page.scroll_to_supporting_claims',
                extra: {
                  source_claim_id: claim.id,
                },
              });
              document
                .getElementById(SUPPORTING_CLAIMS_ELEMENT_ID)
                ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
          >
            Supporting claims ({supportingClaims.length})
          </ScrollToClaimsButton>
        )}

        {contradictingClaims.length > 0 && (
          <ScrollToClaimsButton
            icon="stack"
            onClick={() => {
              trackClick({
                source: 'single_claim_page.scroll_to_contradicting_claims',
                extra: {
                  source_claim_id: claim.id,
                },
              });
              document
                .getElementById(CONTRADICTING_CLAIMS_ELEMENT_ID)
                ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
          >
            Contradictory claims ({contradictingClaims.length})
          </ScrollToClaimsButton>
        )}
      </Card>
    </Container>
  );
};

export default ClaimDetails;
